import { useEffect, useState } from "react";
import { useAppState } from "../../app-state";
import { requestMarketplaceFeeQuote } from "../../lib/api";
import { DEFAULT_POSTAGE } from "../../lib/inscriptions";
import type { TransactionSummary } from "../../lib/types";

const formatter = new Intl.NumberFormat("en-GB");
const sats = (value: number) => `${formatter.format(Math.max(0, Math.round(value)))} sats`;

type Preview = { key: string; fee: number | null; ownershipRequired: boolean };

export function InscriptionFeeSummary({ summary, count, contentBytes, feeRate }: {
  summary?: TransactionSummary; count: number; contentBytes: number; feeRate: number;
}) {
  const { config, wallet } = useAppState();
  const payment = wallet?.payment.address ?? "";
  const ordinals = wallet?.ordinals.address ?? "";
  const key = JSON.stringify([payment, ordinals, config?.marketplaceFeeSats]);
  const enabled = !summary && count > 0 && Boolean(payment && ordinals);
  const [preview, setPreview] = useState<Preview | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let current = true;
    // Display only: asking for eligibility never signs an ownership challenge.
    // The real prepare flow still obtains and validates its own complete quote.
    void requestMarketplaceFeeQuote(payment, ordinals, 1, 1).then(result => {
      const fee = result.ownershipRequired ? 0 : result.quote.marketplaceFeeSats;
      if (!Number.isSafeInteger(fee) || fee < 0) throw new Error("Invalid fee estimate");
      if (current) setPreview({ key, fee, ownershipRequired: result.ownershipRequired });
    }).catch(() => {
      if (current) setPreview({ key, fee: null, ownershipRequired: false });
    });
    return () => { current = false; };
  }, [enabled, key, payment, ordinals]);

  // Key the result to the addresses during render too: an old wallet's zero
  // must not flash for the new wallet before the effect cleanup has run.
  const activePreview = preview?.key === key ? preview : null;
  const perItem = wallet ? activePreview?.fee ?? null : config?.marketplaceFeeSats ?? null;
  const marketplaceFee = count === 0 ? 0 : summary?.marketplaceFee ?? (perItem === null ? null : perItem * count);
  const postage = count === 0 ? 0 : summary?.postage ?? DEFAULT_POSTAGE * count;
  const estimatedNetwork = count === 0 ? 0 : summary?.networkFee ?? Math.ceil((190 + contentBytes * 0.252 + count * 50) * feeRate);
  const total = summary?.total ?? (marketplaceFee === null ? null : marketplaceFee + postage + estimatedNetwork);
  const pendingLabel = activePreview?.fee === null ? "Calculated at review" : "Calculating…";
  return <div className="fee-summary" aria-live="polite">
    <div className="summary-total"><span>{summary ? "Price" : "Estimated price"}</span><strong>{total === null ? pendingLabel : sats(total)}</strong></div>
  </div>;
}
